import { useMemo } from 'react'

import { QuestionnaireStatus } from '@/gen/questionnaire/common/v1/questionnaire_status_pb'

import { questionnaireStatusLabel } from '@/const/label'

import { BadgeProps } from '@/components/lemmala-design-system/badge/badge'

type QuestionnaireStatusLabelResult = {
  label: string
  variant: BadgeProps['variant']
}

/**
 * Gets the detailed questionnaire status label based on answered/total questions and status
 * This ensures consistent status display across assessment views and drawers
 */
export const getQuestionnaireStatusLabel = ({
  status,
  answeredQuestions,
  totalQuestions,
}: {
  status: QuestionnaireStatus
  answeredQuestions: number
  totalQuestions: number
}): QuestionnaireStatusLabelResult => {
  if (status === QuestionnaireStatus.COMPLETED) {
    return { label: questionnaireStatusLabel[status], variant: 'success' }
  }

  if (status === QuestionnaireStatus.SUBMITTED) {
    return { label: questionnaireStatusLabel[status], variant: 'attention' }
  }

  if (status === QuestionnaireStatus.EXPIRED) {
    return { label: 'Closed', variant: 'neutral' }
  }

  if (answeredQuestions === 0) {
    return {
      label: `Not started ${answeredQuestions}/${totalQuestions}`,
      variant: 'neutral',
    }
  }

  return {
    label: `${questionnaireStatusLabel[status]} ${answeredQuestions}/${totalQuestions}`,
    variant: 'promotion',
  }
}

/**
 * React hook that returns a memoised questionnaire status label.
 * The returned object reference changes only when `status`,
 * `answeredQuestions`, or `totalQuestions` change.
 */
export const useQuestionnaireStatusLabel = ({
  status,
  answeredQuestions,
  totalQuestions,
}: {
  status: QuestionnaireStatus
  answeredQuestions: number
  totalQuestions: number
}): QuestionnaireStatusLabelResult =>
  useMemo(
    () => getQuestionnaireStatusLabel({ status, answeredQuestions, totalQuestions }),
    [status, answeredQuestions, totalQuestions],
  )
